






















































































































































import { PropType } from 'vue';
import {
  IAiStream,
  ICalendarRange,
  IVCalendarRef,
  ICalEvent,
  IAiStreamCalendarData
} from '../types';
import {
  GROUP_THRESHOLD,
  CLUSTER_GAP_MIN,
  INTERVAL_HEIGHT,
  DEFAULT_COLORS,
  COLOR_STREAMER_FALLBACK,
  COLOR_DETAIL_FALLBACK
} from '../constants';
import { utcNow, parseUtcMs } from '../utils';
import formatDate from '@/helpers/formatDate';
import CalendarToolbar from './CalendarToolbar.vue';
import CalendarEventCard from './CalendarEventCard.vue';
import CalendarClusterCard from './CalendarClusterCard.vue';
import StreamRow from './StreamRow.vue';
const StreamDetailDialog = (): Promise<any> =>
  import('./StreamDetailDialog.vue');
const StreamClusterDialog = (): Promise<any> =>
  import('./StreamClusterDialog.vue');

export default {
  name: 'AiStreamCalendar',
  components: {
    CalendarToolbar,
    CalendarEventCard,
    CalendarClusterCard,
    StreamRow,
    StreamDetailDialog,
    StreamClusterDialog
  },

  props: {
    events: {
      type: Array as PropType<IAiStream[]>,
      default: (): IAiStream[] => []
    },
    isLoading: { type: Boolean, default: false },
    height: {
      type: [Number, String] as PropType<number | string>,
      default: 680
    },
    timeZone: { type: String, default: 'UTC' }
  },

  data(): IAiStreamCalendarData & { INTERVAL_HEIGHT: number } {
    return {
      INTERVAL_HEIGHT,
      view: 1,
      focus: '',
      nowUtc: utcNow(),
      ready: false,
      timeTimer: undefined,
      dialog: false,
      selected: null,
      selectedColor: '',
      groupDialog: false,
      groupItems: [],
      selectedStreamers: [],
      roster: [],
      weekdays: Object.freeze([1, 2, 3, 4, 5, 6, 0])
    };
  },

  watch: {
    isLoading(val: boolean): void {
      if (val) {
        this.dialog = false;
      }
    },
    events(list: IAiStream[]): void {
      const known = new Set(this.roster);
      const added: string[] = [];
      list.forEach((s: IAiStream) => {
        if (!known.has(s.nickname)) {
          known.add(s.nickname);
          added.push(s.nickname);
        }
      });
      if (added.length) {
        this.roster = [
          ...this.roster,
          ...added.sort((a: string, b: string) => a.localeCompare(b))
        ];
      }
    }
  },

  computed: {
    today(): string {
      return this.nowUtc.split(' ')[0];
    },
    cal(): IVCalendarRef | null {
      return this.ready
        ? (this.$refs.calendar as unknown as IVCalendarRef)
        : null;
    },
    nowY(): string {
      if (!this.cal) return '-10px';
      // Depend on nowUtc (updated by timer) instead of cal.times.now
      // to avoid reactive dependency on calendar internals.
      const [h, m] = this.nowUtc.split(' ')[1].split(':').map(Number);
      return this.cal.timeToY({ hour: h, minute: m }) + 'px';
    },
    streamerColorMap(): Record<string, string> {
      const map: Record<string, string> = {};
      this.roster.forEach((n: string, i: number) => {
        map[n] = DEFAULT_COLORS[i % DEFAULT_COLORS.length];
      });
      return map;
    },
    filteredEvents(): IAiStream[] {
      if (!this.selectedStreamers.length) return this.events;
      const set = new Set(this.selectedStreamers);
      return this.events.filter((s: IAiStream) => set.has(s.nickname));
    },
    clusters(): IAiStream[][] {
      const evs = this.filteredEvents
        .filter((s: IAiStream) => s.startTime && s.endTime)
        .slice()
        .sort((a: IAiStream, b: IAiStream) =>
          (a.startTime || '').localeCompare(b.startTime || '')
        );
      const out: IAiStream[][] = [];
      let cur: IAiStream[] = [];
      let curEnd = -Infinity;
      let curDay = '';
      for (const s of evs) {
        const st = parseUtcMs(s.startTime || '');
        const en = parseUtcMs(s.endTime || '');
        const day = (s.startTime || '').split(' ')[0];
        const gapMs = CLUSTER_GAP_MIN * 60 * 1000;
        if (cur.length && day === curDay && st < curEnd + gapMs) {
          cur.push(s);
          curEnd = Math.max(curEnd, en);
        } else {
          if (cur.length) out.push(cur);
          cur = [s];
          curEnd = en;
          curDay = day;
        }
      }
      if (cur.length) out.push(cur);
      return out;
    },
    displayEvents(): ICalEvent[] {
      const out: ICalEvent[] = [];
      for (const cl of this.clusters) {
        if (cl.length >= GROUP_THRESHOLD) {
          const start = cl.reduce(
            (m: string, s: IAiStream) =>
              (s.startTime || '') < m ? s.startTime || '' : m,
            cl[0].startTime || ''
          );
          const end = cl.reduce(
            (m: string, s: IAiStream) =>
              (s.endTime || '') > m ? s.endTime || '' : m,
            cl[0].endTime || ''
          );
          const heightPx =
            (this.durationMin({ start, end }) * INTERVAL_HEIGHT) / 60;
          out.push({
            name: `${cl.length} streams`,
            start,
            end,
            timed: true,
            color: this.$vuetify.theme.currentTheme.primary as string,
            group: true,
            items: cl,
            visibleRows: Math.max(0, Math.floor((heightPx - 46) / 24))
          });
        } else {
          for (const s of cl) {
            const ev = { start: s.startTime || '', end: s.endTime || '' };
            out.push({
              name: s.nickname,
              start: ev.start,
              end: ev.end,
              timed: true,
              group: false,
              overlapping: cl.length > 1,
              raw: s,
              short: (this.durationMin(ev) * INTERVAL_HEIGHT) / 60 < 46,
              color:
                this.streamerColorMap[s.nickname] || COLOR_STREAMER_FALLBACK
            });
          }
        }
      }
      return out;
    },
    monthLabel(): string {
      return this.cal?.title || '';
    },
    groupedList(): { date: string; label: string; items: IAiStream[] }[] {
      const byDate: Record<string, IAiStream[]> = {};
      this.filteredEvents
        .slice()
        .sort((a: IAiStream, b: IAiStream) =>
          (a.startTime || '').localeCompare(b.startTime || '')
        )
        .forEach((s: IAiStream) => {
          const d = (s.startTime || '').split(' ')[0];
          (byDate[d] = byDate[d] || []).push(s);
        });
      return Object.keys(byDate)
        .sort()
        .map((date: string) => {
          return {
            date,
            label: formatDate(date + ' 00:00', 'EEEE, MMMM d', false, false),
            items: byDate[date]
          };
        });
    }
  },

  mounted(): void {
    this.ready = true;
    this.$nextTick(() => {
      this.scrollToTime();
      this.timeTimer = window.setInterval(() => {
        this.nowUtc = utcNow();
      }, 60 * 1000);
    });
  },

  beforeDestroy(): void {
    if (this.timeTimer) clearInterval(this.timeTimer);
  },

  methods: {
    getEventColor(): string {
      return 'transparent';
    },
    formatInterval(i: { time: string }): string {
      return i.time;
    },

    prev(): void {
      this.cal?.prev();
    },
    next(): void {
      this.cal?.next();
    },
    setToday(): void {
      this.focus = '';
    },
    onRangeChange(payload: {
      start: { date: string };
      end: { date: string };
    }): void {
      const range: ICalendarRange = {
        start: payload.start.date,
        end: payload.end.date
      };

      this.$emit('range-change', range);
    },

    getCurrentTime(): number {
      const [h, m] = this.nowUtc.split(' ')[1].split(':').map(Number);
      return h * 60 + m;
    },
    scrollToTime(): void {
      if (!this.cal) return;
      const t = this.getCurrentTime();
      this.cal.scrollToTime(Math.max(0, t - (t % 30) - 30));
    },

    durationMin(ev: { start: string; end: string }): number {
      return (parseUtcMs(ev.end) - parseUtcMs(ev.start)) / 60000;
    },

    onEventClick({
      nativeEvent,
      event
    }: {
      nativeEvent: Event;
      event: ICalEvent;
    }): void {
      if (event.group && event.items) this.openGroup(event.items);
      else if (event.raw) this.openStream(event.raw);
      nativeEvent.stopPropagation();
    },
    openStream(raw: IAiStream): void {
      this.selected = raw;
      this.selectedColor =
        this.streamerColorMap[raw.nickname] || COLOR_DETAIL_FALLBACK;
      this.dialog = true;
      this.$emit('select', raw);
    },
    openGroup(items: IAiStream[]): void {
      this.groupItems = items;
      this.groupDialog = true;
    },
    openStreamFromGroup(s: IAiStream): void {
      this.$nextTick(() => this.openStream(s));
    },

    onEdit(stream: IAiStream): void {
      this.$emit('edit', stream);
    },
    onCopy(stream: IAiStream): void {
      this.$emit('copy', stream);
    },
    onDelete(stream: IAiStream): void {
      this.$emit('delete', stream);
    }
  }
};
